/**
 * Observability subsystem contract (见 15 / §5.16): the platform-free
 * types and constants that define the unified, structured log record and the
 * cross-process log surface. Kept separate from any concrete sink so this
 * contract can be imported anywhere (main / ext-host / renderer / SDK) without
 * pulling an implementation.
 *
 * Unlike Theme/Nls (single-instance in the extension host), the log *sink* lives
 * in the main process: it must survive an extension-host crash — the very thing
 * it reports — and own log-file persistence. Every process forwards structured
 * {@link LogRecord}s up to that sink (ext-host via `parentPort`, renderer via the
 * `hibro:log:*` IPC bridge).
 */

import type { Event } from './event';

/** Severity of a {@link LogRecord} (ordered: error > warn > info > debug). */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/** Ordered severities, most to least severe — for threshold filtering. */
export const LOG_LEVELS: readonly LogLevel[] = [
  'error',
  'warn',
  'info',
  'debug',
];

/**
 * Where a {@link LogRecord} originated. `module` = an in-process source module
 * (via `context.log`); `iframe`/`external` = a sandboxed module via the gated
 * SDK `log()`; `main`/`ext-host`/`renderer` = platform processes themselves.
 */
export type LogSource =
  | 'main'
  | 'ext-host'
  | 'renderer'
  | 'module'
  | 'iframe'
  | 'external';

/**
 * Kind of record. `log` = ordinary diagnostic; `metric` = a numeric measurement
 * (e.g. module activation duration); `crash` = a process crash / unhandled error
 * report (also surfaced to the user).
 */
export type LogKind = 'log' | 'metric' | 'crash';

/** A single structured, serializable log/observability record (见 15). */
export interface LogRecord {
  /** Epoch milliseconds when the record was produced. */
  ts: number;
  level: LogLevel;
  source: LogSource;
  kind: LogKind;
  /** Origin scope, typically a module id or platform subsystem name. */
  scope?: string;
  message: string;
  /** Optional structured payload (must be JSON-serializable). */
  data?: unknown;
  /** Flattened error info when the record describes a failure. */
  error?: { message: string; stack?: string };
}

/** A record before the sink stamps process-wide defaults (ts/source). */
export type LogInput = Omit<LogRecord, 'ts' | 'source'> &
  Partial<Pick<LogRecord, 'ts' | 'source'>>;

/** Filter for {@link ILogService.query}; omitted fields match everything. */
export interface LogQuery {
  /** Minimum severity to include (e.g. `warn` drops `info`/`debug`). */
  minLevel?: LogLevel;
  source?: LogSource;
  kind?: LogKind;
  scope?: string;
  /** Case-insensitive substring match against `message`. */
  text?: string;
  /** Cap the number of (most-recent) records returned. */
  limit?: number;
}

/**
 * Whether a record at `level` passes a `minLevel` threshold (severity order).
 */
export function meetsLevel(level: LogLevel, minLevel: LogLevel): boolean {
  return LOG_LEVELS.indexOf(level) <= LOG_LEVELS.indexOf(minLevel);
}

/** Apply a {@link LogQuery} to a record (pure, used by the sink + UI). */
export function matchesQuery(record: LogRecord, query: LogQuery): boolean {
  if (query.minLevel && !meetsLevel(record.level, query.minLevel)) {
    return false;
  }
  if (query.source && record.source !== query.source) {
    return false;
  }
  if (query.kind && record.kind !== query.kind) {
    return false;
  }
  if (query.scope && record.scope !== query.scope) {
    return false;
  }
  if (
    query.text &&
    !record.message.toLowerCase().includes(query.text.toLowerCase())
  ) {
    return false;
  }
  return true;
}

/**
 * Cross-process contract for the central log sink (见 15 / §5.16). Lives
 * single-instance in the main process. The renderer reaches it via the
 * `hibro:log:*` IPC bridge rather than the ext-host Channel (the sink is above the
 * ext-host so it outlives ext-host crashes).
 */
export interface ILogService {
  /** Record a log entry (stamps `ts`/`source` defaults when omitted). */
  append(record: LogInput): void;
  /** Snapshot of buffered records matching `query`, oldest-first. */
  query(query?: LogQuery): LogRecord[];
  /** Drop all buffered records. */
  clear(): void;
  /** Fires for every appended record (drives live UI + window push). */
  readonly onDidAppend: Event<LogRecord>;
}

/** Author-facing logger handed to source modules as `context.log` (见 15). */
export interface ModuleLogger {
  error(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  debug(message: string, data?: unknown): void;
}

/** Default ring-buffer capacity for the in-memory log sink. */
export const LOG_BUFFER_LIMIT = 2000;

/**
 * OPFS directory for the browser runtime's daily JSONL logs (见 15 / 18).
 *
 * A sibling of `hibro-modules` at the origin-private root, not a child of it:
 * module trees are sandboxed per id and some engines hold exclusive sync handles
 * there. The desktop writes the same file names under `userData/logs`.
 */
export const LOG_FILES_DIRNAME = 'hibro-logs';

/**
 * How many local calendar days of `hibro-<date>.log` to keep, including today.
 * Older files are removed when the writer starts and when the local date rolls.
 */
export const LOG_FILE_RETENTION_DAYS = 7;

/**
 * Web Lock serializing appends. A version change briefly runs two SharedWorkers;
 * without this both would append to the same file.
 */
export const LOG_FILE_LOCK_NAME = 'hibro:log-file';

const HIBRO_LOG_FILE_NAME = /^hibro-(\d{4}-\d{2}-\d{2})\.log$/;

/** Local calendar day `YYYY-MM-DD` (not UTC), matching the desktop log file. */
export function localLogDateStamp(date: Date): string {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** `hibro-YYYY-MM-DD.log` for the local calendar day of `date`. */
export function hibroLogFileName(date: Date): string {
  return `hibro-${localLogDateStamp(date)}.log`;
}

/** The `YYYY-MM-DD` stamp inside a daily log file name, or `undefined`. */
export function hibroLogFileStamp(name: string): string | undefined {
  return HIBRO_LOG_FILE_NAME.exec(name)?.[1];
}

/** Whether `name` is exactly one daily log file, with no extra path segments. */
export function isHibroLogFileName(name: string): boolean {
  return HIBRO_LOG_FILE_NAME.test(name);
}

/**
 * Reject a download/list name that is not a daily log file.
 * The page reads OPFS itself; this is what keeps that read inside `hibro-logs`.
 */
export function refuseForeignLogFileName(name: string): void {
  if (!isHibroLogFileName(name)) {
    throw new Error(`refused log file name: ${name}`);
  }
}

/**
 * Oldest local-day stamp still kept, inclusive. `retentionDays` counts today.
 */
export function hibroLogRetentionCutoff(
  today: Date,
  retentionDays: number,
): string {
  const shifted = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (retentionDays - 1),
  );
  return localLogDateStamp(shifted);
}

/** IPC channel: renderer -> main, append a record to the sink. */
export const LOG_APPEND_IPC = 'hibro:log:append';

/** IPC channel: renderer -> main (invoke), query buffered records. */
export const LOG_QUERY_IPC = 'hibro:log:query';

/** IPC channel: renderer -> main (invoke), clear the buffer. */
export const LOG_CLEAR_IPC = 'hibro:log:clear';

/** IPC channel: main -> renderer, push a newly appended record. */
export const LOG_CHANGED_IPC = 'hibro:log:changed';

/** IPC channel: renderer -> main (invoke), reveal the on-disk log folder. */
export const LOG_OPEN_FOLDER_IPC = 'hibro:log:open-folder';

/**
 * Control-message type used by the extension host to forward a {@link LogRecord}
 * to the main-process sink over its `parentPort` (mirrors the os-theme/os-locale
 * control messages flowing the other way).
 */
export const LOG_FORWARD_MESSAGE = 'hibro:log:forward';
